/**
 * Trains on the line behind the front — the mission-side half of the original's
 * object class 5.
 *
 * `combat/ground-route.ts` holds the recovered mechanics (25-byte start/end
 * record, one global speed, linear progress, retire at the far end) and
 * docs/game/ground-traffic.md holds the evidence. This file only decides *where*
 * a train runs, which is the one part the original does not tell us: its routes
 * are compiled into `SCRIPT.DAT` by a shell (`PS.EXE`) this port does not have.
 *
 * So placement is drawn from the mission seed through the existing `mission`
 * stream — the same convention every other generated placement uses, and it
 * means a given seed always lays the same track.
 *
 * **Why the patrol.** The original's own shipped scenario, `SCRIPT.DAT`, pairs
 * two aircraft records with two trains, so a sortie that meets aeroplanes *and*
 * ground traffic is exactly what the game shipped. The port's patrol used to
 * carry a note apologising for having no such thing; now it has one.
 */

import { drawGroundRoute, type GroundRoute } from "../combat/targets.ts";
import type { MissionDraw } from "./mission-build.ts";
import type { MissionTarget } from "./mission-spec.ts";

/** Ground height a train's route sits at, and half the train's own height. */
const TRAIN_CENTRE_HEIGHT_M = 3.1;

/**
 * How many trains a sortie meets. `SCRIPT.DAT` carries two, and two is also
 * about as many as the original's 30-object cap leaves room for beside a flight
 * of aeroplanes — so one or two, never a parade.
 */
export const TRAINS_PER_SORTIE: readonly [number, number] = [1, 2];

export interface TrainTrafficOptions {
  /** Mid-point of the band the routes are laid in, in mission `z`. */
  readonly lineZ: number;
  /** Half-width of that band, metres. */
  readonly spreadM: number;
  /** Which side's traffic this is — the trains you are allowed to shoot up. */
  readonly side: "friendly" | "hostile";
  /**
   * Route length is drawn from this range, metres. Defaults to the patrol's own
   * 24-40 km — long enough that a train never runs out of road mid-sortie over
   * the patrol's wide-open working area. A caller whose `lineZ` sits close to
   * the front (a raid's own target site, say) should shorten this: at the
   * default range a route can run its full length along `z` before the bearing
   * draw ever turns it, and a `lineZ` a couple of kilometres from the front is
   * not far enough away to survive that.
   */
  readonly minLengthM?: number;
  readonly maxLengthM?: number;
}

/**
 * One or two trains running lines of communication across the patrol area.
 *
 * Lengths are long — a train that runs out of route while the player is still
 * climbing has retired before it was ever seen — and each one starts part-way
 * along its own line so they are not all setting off together.
 */
export function trainTraffic(draw: MissionDraw, options: TrainTrafficOptions): readonly MissionTarget[] {
  const count = draw.int(TRAINS_PER_SORTIE[0], TRAINS_PER_SORTIE[1]);
  const targets: MissionTarget[] = [];
  for (let index = 0; index < count; index += 1) {
    const drawn = drawGroundRoute(() => draw.float(), {
      lineZ: options.lineZ,
      spreadM: options.spreadM,
      // 24-40 km of line by default: at 11 m/s that is 36-60 minutes of
      // running, so the train is still there for the whole sortie.
      minLengthM: options.minLengthM ?? 24_000,
      maxLengthM: options.maxLengthM ?? 40_000,
      groundY: TRAIN_CENTRE_HEIGHT_M,
    });
    targets.push(trainTarget(`train-${index + 1}`, drawn.route, drawn.startTravelledM, options.side));
  }
  return targets;
}

/** One train on a given route — the `MissionTarget` shape the encounter builds from. */
export function trainTarget(
  id: string,
  route: GroundRoute,
  routeTravelledM: number,
  side: "friendly" | "hostile",
): MissionTarget {
  return {
    id,
    kind: "train",
    side,
    // `createTarget` derives both of these from the route, exactly as the
    // original's class-5 constructor does; they are filled in here so the spec
    // stays readable on its own and so anything reading a `MissionTarget`
    // without building an encounter still gets a sane pose.
    position: route.start,
    headingRad: route.headingRad,
    label: "Train",
    route,
    routeTravelledM,
  };
}
